import { Typography, Flex, Spin, Tag, Popover, Select, DatePicker } from 'antd'
import { CSSProperties, MouseEvent, memo, useCallback, useMemo, useState } from 'react'
import { FileTextOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { useDraggable } from '@dnd-kit/core'
import dayjs, { Dayjs } from 'dayjs'
import { useRescheduleCandidatesQuery } from '../scheduleQueries'
import { RescheduleCandidate, RescheduleReasons } from '../scheduleApi'
import CreateEventFromJobModal from './CreateEventFromJobModal'
import {
  ScheduleCreateEventFilters,
  filterRescheduleCandidates,
  getRescheduleCandidatesDateRange,
  RescheduleRangeType,
  rescheduleCandidateToInitialEvent,
  rescheduleCandidateToSchedulingJob,
} from './scheduleCreateEventModel'
import { VisitWorkareaStatuses, visitWorkareaStatusName } from '../../../common/constants'
import './RescheduleCandidatesList.css'

const { Text } = Typography
const { RangePicker } = DatePicker

const rowStyle: CSSProperties = {
  padding: '12px',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  borderBottom: '1px solid #f0f0f0',
}

type Props = {
  filters: ScheduleCreateEventFilters
  onEventCreated?: () => void
  selectedDate?: Dayjs
}

const RescheduleCandidatesList = ({ filters, onEventCreated, selectedDate }: Props) => {
  const [rangeType, setRangeType] = useState<RescheduleRangeType>('last7')
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [selectedCandidate, setSelectedCandidate] = useState<RescheduleCandidate | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const yesterday = useMemo(() => dayjs().subtract(1, 'day').startOf('day'), [])

  const { startDate, endDate } = useMemo(
    () => getRescheduleCandidatesDateRange(rangeType, customRange, yesterday),
    [rangeType, customRange, yesterday]
  )

  const startDateStr = startDate ? startDate.format('YYYY-MM-DD') : ''
  const endDateStr = endDate ? endDate.format('YYYY-MM-DD') : ''

  const { data, isLoading } = useRescheduleCandidatesQuery(startDateStr, endDateStr)

  const filteredCandidates = useMemo(() => {
    return filterRescheduleCandidates(data || [], filters)
  }, [data, filters])

  const handleCandidateClick = useCallback((candidate: RescheduleCandidate) => {
    setSelectedCandidate(candidate)
    setIsModalOpen(true)
  }, [])

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedCandidate(null)
  }

  const disabledDate = (current: Dayjs) => current && current.isAfter(yesterday, 'day')

  return (
    <>
      <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Select
          value={rangeType}
          onChange={(value) => setRangeType(value)}
          style={{ width: '100%' }}
          options={[
            { value: 'last7', label: 'Last 7 days' },
            { value: 'last30', label: 'Last 30 days' },
            { value: 'custom', label: 'Custom range' },
          ]}
        />
        {rangeType === 'custom' && (
          <RangePicker
            value={customRange}
            onChange={(dates) => setCustomRange(dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null)}
            disabledDate={disabledDate}
            allowClear={false}
            style={{ width: '100%' }}
          />
        )}
      </div>

      {isLoading ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: 12 }}>
          <Spin />
        </div>
      ) : rangeType === 'custom' && !customRange ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
          Select a custom date range
        </div>
      ) : filteredCandidates.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
          No events need to be rescheduled
        </div>
      ) : (
        <div>
          {filteredCandidates.map((candidate) => (
            <DraggableRescheduleCandidateRow
              key={candidate.calendarEventId}
              candidate={candidate}
              onCandidateClick={handleCandidateClick}
            />
          ))}
        </div>
      )}

      {isModalOpen && selectedCandidate && (
        <CreateEventFromJobModal
          open={true}
          onClose={handleModalClose}
          job={rescheduleCandidateToSchedulingJob(selectedCandidate)}
          onSuccess={onEventCreated}
          selectedDate={selectedDate}
          rescheduleFromEventId={selectedCandidate.calendarEventId}
          initialEvent={rescheduleCandidateToInitialEvent(selectedCandidate, selectedDate || dayjs())}
        />
      )}
    </>
  )
}

type RowProps = {
  candidate: RescheduleCandidate
  onCandidateClick: (candidate: RescheduleCandidate) => void
}

const DraggableRescheduleCandidateRow = memo(({ candidate, onCandidateClick }: RowProps) => {
  const [expanded, setExpanded] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `reschedule-${candidate.calendarEventId}`,
    data: { type: 'reschedule-candidate', candidate },
  })

  const addressParts = [candidate.address1, candidate.address2, candidate.city, candidate.state, candidate.zip].filter(Boolean)
  const address = addressParts.join(', ')

  const style: CSSProperties = {
    ...rowStyle,
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  }

  const handleToggleExpand = (e: MouseEvent) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  const scheduledDate = dayjs(candidate.scheduledStartUtc).format('MMM D, YYYY')

  return (
    <div>
      <div
        ref={setNodeRef}
        className="reschedule-candidate-row"
        style={style}
        onClick={() => !isDragging && onCandidateClick(candidate)}
        {...listeners}
        {...attributes}
      >
        <Flex gap={8} align="start">
          <div style={{ flex: 1 }}>
            <Flex justify="space-between" align="center">
              <Flex gap={6} align="center">
                <Text strong>{candidate.jobName}</Text>
                {candidate.schedulingNotes && (
                  <Popover content={candidate.schedulingNotes} trigger="hover" styles={{ root: { maxWidth: 224 } }}>
                    <FileTextOutlined
                      style={{ fontSize: '14px', color: '#1677ff', cursor: 'pointer' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popover>
                )}
              </Flex>
              {candidate.jobNumber && (
                <Flex gap={8} align="center" style={{ flexShrink: 0 }}>
                  <Text type="secondary" style={{ fontSize: '14px' }}>{candidate.jobNumber}</Text>
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    style={{ color: '#1677ff', cursor: 'pointer', fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`/#!/job-redirect/${candidate.jobId}`, '_blank')
                    }}
                  />
                </Flex>
              )}
            </Flex>
            <div>{candidate.customerDisplayName}</div>
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>{address}</Text>
            </div>
            <div onClick={handleToggleExpand} style={{ marginTop: 4 }}>
              <Text style={{ fontSize: '12px', color: '#1677ff', cursor: 'pointer' }}>
                {expanded ? 'Hide Details' : 'Show Details'}
              </Text>
            </div>
          </div>
          {!candidate.jobNumber && (
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              style={{ color: '#1677ff', cursor: 'pointer', padding: '6px 0' }}
              onClick={(e) => {
                e.stopPropagation()
                window.open(`/#!/job-redirect/${candidate.jobId}`, '_blank')
              }}
            />
          )}
        </Flex>
      </div>
      {expanded && (
        <div style={{ padding: '8px 12px 12px 24px', backgroundColor: '#fafafa' }}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Originally scheduled:</Text>{' '}
            <Text style={{ fontSize: '12px' }}>{scheduledDate}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>Reason:</Text>
            {candidate.reason === RescheduleReasons.NoVisitRecorded ? (
              <span style={{ fontSize: '12px', marginTop: 4 }}>{' '}No visit recorded</span>
            ) : (
              <div style={{ marginTop: 4 }}>
                {candidate.incompleteWorkAreas.map((wa) => (
                  <Flex
                    key={wa.workareaId}
                    justify="space-between"
                    align="center"
                    style={{ padding: '4px 0', gap: 8 }}
                  >
                    <Text style={{ fontSize: '12px' }}>{wa.workareaName}</Text>
                    <Tag
                      color={wa.status === VisitWorkareaStatuses.InProgress ? 'blue' : 'orange'}
                      bordered={true}
                      style={{ fontSize: '11px', margin: 0 }}
                    >
                      {visitWorkareaStatusName(wa.status)}
                    </Tag>
                  </Flex>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export default RescheduleCandidatesList
